import React from "react";
import { MapPin, ExternalLink } from "lucide-react";
import { fmtUSD, fmtNum } from "../../lib/format";
import { StreetViewImage } from "./StreetViewImage";

export default function CompsGrid({ comparables }) {
  if (!comparables?.length) return null;
  return (
    <div data-testid="comps-section">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="eyebrow">Recent Comparable Sales</span>
          <h2 className="mt-2 font-serif text-2xl sm:text-3xl text-brand-ink">How similar homes sold nearby</h2>
        </div>
        <span data-testid="comps-sort-label"
          className="shrink-0 rounded-full border border-brand-hairline bg-brand-cream px-3 py-1 text-[0.7rem] font-medium text-brand-stone/80">
          Sorted by Relevance
        </span>
      </div>
      <p className="mt-2 text-sm text-brand-stone max-w-2xl">Illustrative demonstration comparables. Active listings are never shown as closed sales. Verified closed-sale data requires licensed access.</p>
      <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {comparables.map((c, i) => (
          <a key={i} data-testid="comp-sale-card"
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(c.lat && c.lng ? `${c.lat},${c.lng}` : c.address)}`}
            target="_blank" rel="noopener noreferrer"
            className="group rounded-2xl bg-white border border-brand-hairline overflow-hidden shadow-[0_4px_20px_rgba(27,59,43,0.04)] hover:shadow-[0_12px_36px_rgba(27,59,43,0.10)] hover:border-brand-emerald transition-all flex flex-col cursor-pointer">
            <div className="relative h-28 bg-gradient-to-br from-brand-emerald to-brand-dark flex items-center justify-center overflow-hidden">
              <StreetViewImage lat={c.lat} lng={c.lng} w={600} h={224}
                alt={`Street view of ${c.address}`}
                className="absolute inset-0 h-full w-full object-cover" />
              <span className="font-serif text-5xl text-brand-cream/20">{i + 1}</span>
              <span className="absolute top-3 right-3 z-10 inline-flex items-center gap-1 rounded-full bg-black/40 backdrop-blur-sm px-2 py-0.5 text-[0.6rem] font-medium text-white">
                <ExternalLink className="h-3 w-3" /> View on map
              </span>
              {c.same_complex && (
                <span className="absolute top-3 left-3 rounded-full bg-brand-gold text-white px-2.5 py-0.5 text-[0.65rem] font-semibold z-10">Same complex</span>
              )}
            </div>
            <div className="p-5 flex flex-col flex-1">
              <p className="font-mono text-xl font-bold text-brand-emerald">{fmtUSD(c.sale_price)}</p>
              <p className="mt-1 flex items-start gap-1.5 text-sm text-brand-ink"><MapPin className="h-3.5 w-3.5 text-brand-gold mt-0.5 shrink-0" />{c.address}</p>
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-brand-stone">
                <span>{c.beds} bd</span><span>{c.baths} ba</span><span>{fmtNum(c.living_area_sqft)} sqft</span>
                <span>{fmtUSD(c.price_per_sqft)}/sqft</span>
              </div>
              <div className="mt-3 flex items-center justify-between text-[0.7rem] text-brand-stone/70 border-t border-brand-hairline pt-3">
                <span>Sold {c.sale_date}</span>
                <span className="flex items-center gap-2">
                  <span>{c.distance_miles} mi away</span>
                  <span className="inline-flex items-center gap-1 text-brand-emerald font-medium opacity-60 group-hover:opacity-100 transition-opacity">Map <ExternalLink className="h-3 w-3" /></span>
                </span>
              </div>
              {/* MLS compliance footer: broker attribution + IDX / Broker Reciprocity placeholder */}
              <div className="mt-auto pt-3" data-testid="comp-attribution">
                <div className="flex items-center justify-between gap-2 border-t border-brand-hairline pt-3">
                  <p className="text-[0.65rem] leading-snug text-brand-stone/70">
                    {c.listing_office
                      ? <>Listing courtesy of <span className="font-medium text-brand-stone">{c.listing_office}</span></>
                      : <span className="italic text-brand-stone/50">Broker attribution pending licensed MLS feed</span>}
                  </p>
                  <span data-testid="comp-idx-logo" title="IDX / Broker Reciprocity"
                    className="shrink-0 inline-flex items-center rounded border border-brand-hairline bg-brand-cream px-1.5 py-0.5 text-[0.55rem] font-semibold tracking-wide text-brand-stone/50">
                    IDX
                  </span>
                </div>
                <p className="mt-2 text-[0.6rem] text-brand-stone/50">Source: {c.source}</p>
              </div>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}
